import { useQuery } from "@tanstack/react-query";
import type { DeliveryReviewDTO, MissionReviewDTO, ReviewViewState } from "@bgreen/shared";
import { get, post } from "../lib/api";
import { fmtDateTime, fmtDuration, fmtTokens, shortSha } from "../lib/format";
import { useAction } from "../lib/hooks";
import { Button } from "./ui/button";
import { Chip, Details, H2, Panel } from "./ui/primitives";

const STATE: Record<ReviewViewState, { label: string; tone: "neutral" | "good" | "warn" | "bad" | "info" }> = {
  disabled: { label: "Desactivada", tone: "neutral" },
  not_selected: { label: "Sin revisión aún", tone: "neutral" },
  skipped: { label: "Omitida", tone: "neutral" },
  pending: { label: "Pendiente", tone: "info" },
  running: { label: "Ejecutándose", tone: "info" },
  completed: { label: "Completada", tone: "good" },
  failed: { label: "Fallida", tone: "bad" },
  outdated: { label: "Desactualizada", tone: "warn" },
  exhausted: { label: "Rondas agotadas", tone: "warn" },
};
const SEV: Record<string, "bad" | "warn" | "info" | "neutral"> = { critical: "bad", high: "bad", medium: "warn", low: "neutral" };
const CAT: Record<string, string> = { behavior: "comportamiento", requirement: "requisito", security: "seguridad", missing_test: "prueba ausente" };

/**
 * Revisión técnica independiente (Fase 3, modo sombra). Muestra el estado para el commit actual, el informe validado, las rondas y el consumo.
 * Con la función desactivada y sin historial no se pinta nada.
 */
export function ReviewPanel({ missionId }: { missionId: string }) {
  const q = useQuery({ queryKey: ["mission-review", missionId], queryFn: () => get<MissionReviewDTO>(`/missions/${missionId}/review`), refetchInterval: 10_000 });
  const retry = useAction(() => post(`/missions/${missionId}/review/retry`), { ok: "Revisión solicitada: se ejecutará en el siguiente ciclo.", invalidate: [["mission-review", missionId]] });
  const v = q.data;
  if (!v || (!v.enabled && !v.history.length)) return null;
  const st = STATE[v.state];
  const cur = v.current;
  const older = v.history.filter((r) => r.id !== cur?.id);
  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <H2 className="mb-0">Revisión independiente</H2>
        <span className="flex flex-wrap items-center gap-2"><Chip tone="neutral" title="Los hallazgos no bloquean la misión ni cambian aprobaciones ni condiciones de merge; el consumo del revisor sí cuenta en los presupuestos">Modo sombra</Chip><Chip tone={st.tone}>{st.label}</Chip></span>
      </div>
      <p className="m-0 mt-2 max-w-[75ch] text-[14px] text-mu">{v.stateDetail}{cur?.statusReason && cur.status !== "completed" && <span className="ml-1.5 font-mono text-[11px]">({cur.statusReason.code})</span>}</p>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[12px] text-mu">
        <span>commit actual {shortSha(v.currentHeadSha)}</span>
        {cur && <span>revisado {shortSha(cur.headSha)}</span>}
        <span>ejecuciones {v.rounds.executionsUsed}/{v.rounds.executionsMax}</span>
        <span>correcciones autorizadas {v.rounds.correctionsAuthorized} (máx. {v.rounds.correctionsMax} con revisión)</span>
        {!v.providerConfigured && v.enabled && <span className="text-wn">revisor no configurado</span>}
      </div>
      {cur && <ReviewBody r={cur} />}
      {v.enabled && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button disabled={!v.canRetry} loading={retry.isPending} onClick={() => retry.mutate()}>Pedir otra revisión</Button>
          {!v.canRetry && v.retryBlockedReason && <span className="text-[13px] text-mu">{v.retryBlockedReason}</span>}
        </div>
      )}
      {older.length > 0 && (
        <Details title={`Historial (${older.length}) — los informes de otros commits no validan la entrega actual`}>
          <div className="flex flex-col gap-3">{older.map((r) => <HistoryRow key={r.id} r={r} />)}</div>
        </Details>
      )}
    </Panel>
  );
}

function Usage({ r }: { r: DeliveryReviewDTO }) {
  if (!r.usage) return <span>sin consumo (no se lanzó)</span>;
  if (r.usage.state === "unknown") return <span className="text-wn">consumo desconocido (no es 0)</span>;
  return <span>{fmtTokens(r.usage.total ?? 0)} tok medidos · entrada {fmtTokens(r.usage.input ?? 0)} · salida {fmtTokens(r.usage.output ?? 0)}</span>;
}

function ReviewBody({ r }: { r: DeliveryReviewDTO }) {
  return (
    <div className="mt-3 border-t border-ln pt-3 text-[14px]">
      <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-[12px] text-mu">
        {r.decision.action === "review" && <span>seleccionada: {r.decision.reason}</span>}
        {r.executionNo && <span>ejecución {r.executionNo}{r.authorization ? ` · ${r.authorization.detail}` : ""}</span>}
        {r.model && <span>modelo solicitado {r.model}</span>}
        {r.execution && <span>{fmtDuration(r.execution.durationMs)}</span>}
        <Usage r={r} />
      </div>
      {r.status === "completed" && (
        <>
          {r.summary && <p className="m-0 mt-2 text-[13px] text-mu">Resumen del revisor (contenido no confiable): {r.summary}</p>}
          {r.findings.length === 0 ? (
            <p className="m-0 mt-2 text-[14px]">Sin hallazgos válidos. <span className="text-mu">No es una garantía de ausencia de defectos.</span></p>
          ) : (
            <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
              {r.findings.map((f, i) => (
                <li key={i} className="rounded-card border border-ln px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2"><Chip tone={SEV[f.severity] ?? "neutral"}>{f.severity}</Chip><Chip>{CAT[f.category] ?? f.category}</Chip>{f.confidence === "suspected" && <Chip tone="warn">sospecha</Chip>}{f.wouldBlock && <Chip tone="bad" title="En un modo activo (no implementado) bloquearía; en sombra no bloquea">bloquearía</Chip>}<b className="text-[14px]">{f.title}</b></div>
                  <div className="mt-1 font-mono text-[12px] text-mu">{f.file}:{f.line}</div>
                  <div className="mt-1 text-[13px]"><b>Evidencia:</b> {f.evidence}</div>
                  <div className="text-[13px]"><b>Efecto:</b> {f.effect}</div>
                  <div className="text-[13px] text-mu"><b>Justificación:</b> {f.rationale}</div>
                </li>
              ))}
            </ul>
          )}
          {r.rejectedFindings.length > 0 && (
            <Details title={`Descartados por formato o referencias inexistentes (${r.rejectedFindings.length})`}>
              <ul className="m-0 list-disc pl-5 text-[13px] text-mu">{r.rejectedFindings.map((x, i) => <li key={i}>{x.reason}</li>)}</ul>
            </Details>
          )}
          {(r.uncertainties.length > 0 || (r.coverage && !r.coverage.complete)) && (
            <Details title="Incertidumbres y cobertura">
              <ul className="m-0 list-disc pl-5 text-[13px] text-mu">
                {r.uncertainties.map((u, i) => <li key={i}>{u}</li>)}
                {r.coverage?.notReviewed.map((n, i) => <li key={`n${i}`}>No revisado: <span className="font-mono">{n.path}</span> — {n.reason}</li>)}
              </ul>
            </Details>
          )}
        </>
      )}
    </div>
  );
}

function HistoryRow({ r }: { r: DeliveryReviewDTO }) {
  return (
    <div className="border-t border-ln pt-2 text-[13px] first:border-t-0">
      <div className="flex flex-wrap items-center gap-2"><Chip>{r.status}</Chip><span className="font-mono text-[12px]">{shortSha(r.headSha)}</span>{r.superseded && <span className="text-wn">histórico (commit {shortSha(r.superseded.bySha)})</span>}<span className="text-mu">{fmtDateTime(r.createdAt)}</span></div>
      <div className="mt-1 text-mu">{r.statusReason?.detail ?? r.decision.reason}{r.status === "completed" ? ` · ${r.findings.length} hallazgo(s)` : ""} · <Usage r={r} /></div>
    </div>
  );
}
